import { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import { weakMapMemoize } from '@reduxjs/toolkit'
import {
  BoxGeometry,
  BufferGeometry,
  CylinderGeometry,
  DoubleSide,
  ExtrudeGeometry,
  Line3,
  MathUtils,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Raycaster,
  Shape,
  Triangle,
  Vector2,
  Vector2Like,
  Vector3,
  Vector3Like,
} from 'three'
import { graphql } from '~/gql'
import {
  Door,
  ElevationLine,
  ElevationPoint,
  Obstruction,
  Product,
  Roof,
  RoofSection,
  UtilityBox,
  Wall,
  WallSegment,
} from '~/store/objects/types'
import { modelToUI, vectorUIToModel } from '../util/units'
import Util from './util'
import cdt2d from 'cdt2d'
import { Edge, isPointInPolygon } from '~/utils/pointInPolygon'
import { ProductDistanceEngineHeaterDataQuery } from '~/gql/graphql'
import { wallIsValid } from '~/store/objects/walls'
import { getInteriorWallFullHeight, getSegmentOffsets } from '~/components/DrawingCanvas/Walls/util'

type Tuple<T, L extends number> = L extends L ? (number extends L ? T[] : _TupleOf<T, L, []>) : never
type _TupleOf<T, L extends number, R extends unknown[]> = R['length'] extends L ? R : _TupleOf<T, L, [T, ...R]>

function slidingWindow<T, L extends number>(array: T[], windowSize: L): Tuple<T, L>[] {
  return Array.from({ length: array.length - (windowSize - 1) }, (_, idx) =>
    array.slice(idx, idx + windowSize)
  ) as Tuple<T, L>[]
}

function negate({ x, y, z }: Vector3Like) {
  return { x: -x, y: -y, z: -z }
}
function add({ x: x1, y: y1, z: z1 }: Vector3Like, { x: x2, y: y2, z: z2 }: Vector3Like) {
  return { x: x1 + x2, y: y1 + y2, z: z1 + z2 }
}
function sub(a: Vector3Like, b: Vector3Like) {
  return add(a, negate(b))
}
function magnitude({ x, y, z }: Vector3Like) {
  return Math.sqrt(x * x + y * y + z * z)
}

function pointInPolygon(point: Vector2Like, points: Vector2Like[]) {
  // ray-casting algorithm based on
  // http://www.ecse.rpi.edu/Homepages/wrf/Research/Short_Notes/pnpoly.html

  const x = point.x
  const y = point.y

  let inside = false
  let i = 0
  for (let j = points.length - 1; i < points.length; j = i - 1) {
    const xi = points[i].x
    const yi = points[i].y
    const xj = points[j].x
    const yj = points[j].y

    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi
    if (intersect) inside = !inside

    i += 1
  }

  return inside
}

export function wallContainingPoint(
  position: Vector3Like,
  walls: Readonly<Record<string, Wall>>,
  segments: Readonly<Record<string, WallSegment>>
): string | null {
  for (const wall of Object.values(walls)) {
    // if it's not a wall we don't care
    if (wall.layerKey !== 'EXTERIOR_WALLS') {
      continue
    }
    // if it's not enclosed we also don't care
    if (!wall.isEnclosed) {
      continue
    }
    const segmentIDs = wall.segments

    // search for a discontinuity
    const windows = slidingWindow(segmentIDs, 2)
    let discontinuity: [string, string] | undefined = undefined
    for (const [prevID, nextID] of windows) {
      const nextSegment = segments[nextID]
      const prevSegment = segments[prevID]

      if (magnitude(sub(prevSegment.endPoint, nextSegment.startPoint)) >= 0.01) {
        discontinuity = [prevID, nextID]
        break
      }
    }

    // if there's a discontinuity we obviously can't test it for inclusion
    if (discontinuity !== undefined) {
      continue
    }

    // we reconstruct the walls the way the DrawingCanvas/lib/wall.js does them
    const polygonPoints: Vector3Like[] = []

    segmentIDs.forEach(segment => {
      polygonPoints.push(segments[segment].startPoint)
    })

    const lastSegment = segmentIDs[segmentIDs.length - 1]
    if (lastSegment) {
      polygonPoints.push(segments[lastSegment].endPoint)
    }

    if (pointInPolygon(position, polygonPoints)) {
      return wall.id
    }
  }
  return null
}

export type WallSegmentWithShapePoints = WallSegment & {
  insetEdge: { start: Vector2; end: Vector2 }
  outsetEdge: { start: Vector2; end: Vector2 }
}

export const denormalizeWalls = (
  segments: Readonly<Record<string, WallSegment>>,
  walls: Readonly<Record<string, Wall>>
): (Omit<Wall, 'segments'> & { segments: WallSegmentWithShapePoints[] })[] => {
  const validWalls = Object.values(walls).filter(wall => wallIsValid(segments, wall))
  return validWalls.map(wall => {
    const _segments = wall.segments.map(segmentId => segments[segmentId])
    const { insetEdges, outsetEdges } = getSegmentOffsets(_segments)
    const denormalizedSegments = _segments.map((segment, i) => {
      const [insetStart, insetEnd] = insetEdges[i]
      const [outsetStart, outsetEnd] = outsetEdges[i]
      return {
        ...segment,
        insetEdge: { start: new Vector2(...insetStart), end: new Vector2(...insetEnd) },
        outsetEdge: { start: new Vector2(...outsetStart), end: new Vector2(...outsetEnd) },
      }
    })
    return { ...wall, segments: denormalizedSegments }
  })
}

function segmentMesh(segment: Readonly<WallSegmentWithShapePoints>, interiorWallFullHeight?: number) {
  const shape = new Shape()
    .moveTo(segment.insetEdge.start.x, segment.insetEdge.start.y)
    .lineTo(segment.insetEdge.end.x, segment.insetEdge.end.y)
    .lineTo(segment.outsetEdge.end.x, segment.outsetEdge.end.y)
    .lineTo(segment.outsetEdge.start.x, segment.outsetEdge.start.y)
    .closePath()

  const mesh = new Mesh(
    new ExtrudeGeometry(shape, {
      steps: 1,
      depth: segment.isFullHeight && interiorWallFullHeight ? interiorWallFullHeight : segment.height,
      bevelEnabled: false,
    })
  )

  mesh.userData['segmentID'] = segment.id

  return mesh
}

export const memoizedSegmentMesh = weakMapMemoize(segmentMesh)

function wallMeshes(segments: Readonly<Record<string, WallSegment>>, walls: Readonly<Record<string, Wall>>) {
  const denormalizedWalls = denormalizeWalls(segments, walls)
  const segmentMeshes = denormalizedWalls
    .map(wall => {
      const interiorWallFullHeight = getInteriorWallFullHeight(segments)
      return wall.segments.map(segment => [segment.id, memoizedSegmentMesh(segment, interiorWallFullHeight)] as const)
    })
    .flat()
  return new Map(segmentMeshes)
}

export const memoizedWallMeshes = weakMapMemoize(wallMeshes)

export const getHeaterDimensions = (
  heaterQueryData: ProductDistanceEngineHeaterDataQuery['ProductVariation']['heaterData'],
  radiantHeaterRotation?: number
) => {
  const isUnitHeater = radiantHeaterRotation === undefined
  if (isUnitHeater) {
    const {
      uhClearanceNonAccessSide,
      boxWidthB,
      uhClearanceAccessPanel,
      uhClearanceFlueConnector,
      blowerDepthE,
      uhClearanceRear,
      uhClearanceTop,
      boxHeightA,
      uhClearanceBottom,
    } = heaterQueryData[0]
    return {
      width: uhClearanceNonAccessSide! + boxWidthB! + uhClearanceAccessPanel!,
      depth: uhClearanceFlueConnector! + blowerDepthE! + uhClearanceRear!,
      height: uhClearanceTop! + boxHeightA! + uhClearanceBottom!,
    }
  } else {
    const {
      minTubeLength,
      burnerBoxWidth,
      burnerBoxClearanceWidth,
      irhClearanceB,
      tubeDiameter,
      irhClearanceD,
      irhClearanceC,
    } = heaterQueryData.find(
      ({ angle }) => angle === Math.abs(radiantHeaterRotation) || (angle === 90 && radiantHeaterRotation === 0)
    )!
    const sixInchClearance = 6
    return {
      width: minTubeLength! + burnerBoxWidth! + burnerBoxClearanceWidth! + sixInchClearance,
      depth: irhClearanceB! + tubeDiameter! + irhClearanceD!,
      height: irhClearanceC!,
    }
  }
}

async function productMesh(client: ApolloClient<NormalizedCacheObject>, product: Readonly<Product>): Promise<Mesh> {
  const {
    data: { ProductVariation },
  } = await client.query({
    query: graphql(`
      query ProductDistanceEngineCategoryAndModel($variationID: ID!) {
        ProductVariation(id: $variationID) {
          id
          product {
            category
            model
          }
        }
      }
    `),
    variables: { variationID: product.variationId },
  })
  const {
    product: { category, model },
  } = ProductVariation
  if (category === 'HEAT') {
    const { data } = await client.query({
      query: graphql(`
        query ProductDistanceEngineHeaterData($variationID: ID!) {
          ProductVariation(id: $variationID) {
            id
            heaterData {
              id
              angle
              blowerDepthE
              boxHeightA
              boxWidthB
              burnerBoxClearanceWidth
              burnerBoxWidth
              irhClearanceB
              irhClearanceC
              irhClearanceD
              minTubeLength
              tubeDiameter
              uhClearanceAccessPanel
              uhClearanceBottom
              uhClearanceFlueConnector
              uhClearanceNonAccessSide
              uhClearanceRear
              uhClearanceTop
            }
          }
        }
      `),
      variables: {
        variationID: product.variationId,
      },
    })
    const { width, depth, height } = getHeaterDimensions(
      data.ProductVariation.heaterData,
      model === 'Unit Heater' ? undefined : product.rotation.x
    )
    const clearanceBox = new BoxGeometry(width, depth, height, 32)

    if (product.rotation.y === 90 || product.rotation.y === 270) clearanceBox.rotateZ(Math.PI / 2)
    const mesh = new Mesh(clearanceBox)
    mesh.position.copy(product.position)
    mesh.geometry.computeBoundingSphere()
    mesh.updateMatrixWorld()

    return mesh
  } else {
    const { data } = await client.query({
      query: graphql(`
        query ProductDistanceEngineFanData($variationID: ID!) {
          ProductVariation(id: $variationID) {
            id
            size
          }
        }
      `),
      variables: {
        variationID: product.variationId,
      },
    })

    const radius = data.ProductVariation.size / 2
    const clearanceHeight = data.ProductVariation.size * 2
    const clearanceCylinder = new CylinderGeometry(radius, radius, clearanceHeight, 32)

    clearanceCylinder.rotateX(Math.PI / 2)
    const mesh = new Mesh(clearanceCylinder)
    mesh.position.copy(product.position)
    mesh.geometry.computeBoundingSphere()
    mesh.updateMatrixWorld()

    return mesh
  }
}

export const memoizedProductMesh = weakMapMemoize(productMesh)

async function productMeshes(client: ApolloClient<NormalizedCacheObject>, products: Readonly<Record<string, Product>>) {
  const entries = await Promise.all(
    Object.values(products).map(async product => [product.id, await memoizedProductMesh(client, product)] as const)
  )
  return new Map(entries)
}

export const memoizedProductMeshes = weakMapMemoize(productMeshes)

function doorMesh(door: Readonly<Door>, wall: Readonly<WallSegment>) {
  const { x, y } = door.position
  const z = door.height / 2

  const { width, height } = door
  const thickness = wall.thickness + 0.2

  if (door.doorType === 'rollbackDoor') {
    const offset = Math.abs(height / 2.0)

    const geometry = new BoxGeometry(width, height, thickness / 2)
    const mesh = new Mesh(geometry)
    mesh.position.set(x - offset, y, z + offset)

    return mesh
  } else if (door.doorType === 'verticalDoor') {
    const offset = Math.abs(thickness / 2.0)

    const geometry = new BoxGeometry(thickness / 2, width, height)
    const mesh = new Mesh(geometry)
    mesh.position.set(x - offset, y, z + height)

    return mesh
  } else {
    return new Object3D()
  }
}

export const memoizedDoorMesh = weakMapMemoize(doorMesh)

function doorMeshes(doors: Readonly<Record<string, Door>>, walls: Readonly<Record<string, WallSegment>>) {
  return new Map(
    Object.values(doors)
      .filter(
        door => (door.doorType === 'rollbackDoor' || door.doorType === 'verticalDoor') && door.wallSegmentId in walls
      )
      .map(door => [door.id, memoizedDoorMesh(door, walls[door.wallSegmentId])])
  )
}

export const memoizedDoorMeshes = weakMapMemoize(doorMeshes)

function applyObstructionHeight(
  obstruction: Obstruction,
  roofs: ReturnType<typeof memoizedRoofMeshes>,
  position: Vector3
) {
  if (obstruction.startLocation !== 'ceiling') {
    position.setZ(0)
    return
  }

  let distance = Infinity

  const origin = new Vector3().copy(obstruction.position)
  const up = new Vector3(0, 0, 1)
  const raycaster = new Raycaster(origin, up)

  for (const roof of roofs.values()) {
    const intersections = raycaster.intersectObject(roof)
    for (const intersection of intersections) {
      if (intersection.distance <= distance) {
        distance = intersection.distance
        position.setZ(intersection.point.z - obstruction.height)
      }
    }
  }
}

function obstructionMesh(obstruction: Readonly<Obstruction>, roofs: ReturnType<typeof memoizedRoofMeshes>) {
  const points = obstruction.positions.map(pt => {
    const { x, y } = new Vector3().copy(pt).sub(obstruction.position)
    return new Vector2(x, y)
  })
  const geometry = new ExtrudeGeometry(new Shape(points), { depth: obstruction.height })
  const mesh = new Mesh(geometry)
  mesh.position.copy(obstruction.position)
  mesh.rotation.set(0, 0, MathUtils.degToRad(obstruction.rotation.z))
  applyObstructionHeight(obstruction, roofs, mesh.position)
  mesh.geometry.computeBoundingBox()
  mesh.updateMatrixWorld()
  return mesh
}

export const memoizedObstructionMesh = weakMapMemoize(obstructionMesh)

function obstructionMeshes(
  obstructions: Readonly<Record<string, Obstruction>>,
  roofs: Readonly<Record<string, Roof>>,
  lines: Readonly<Record<string, ElevationLine>>,
  points: Readonly<Record<string, ElevationPoint>>
) {
  const roofMeshes = memoizedRoofMeshes(roofs, lines, points)

  return new Map(
    Object.values(obstructions).map(obstruction => [obstruction.id, memoizedObstructionMesh(obstruction, roofMeshes)])
  )
}

export const memoizedObstructionMeshes = weakMapMemoize(obstructionMeshes)

const UTILITY_BOX_DEFAULT_THICKNESS = 24

function utilityBoxMesh(box: Readonly<UtilityBox>) {
  const thickness = (box.thickness || UTILITY_BOX_DEFAULT_THICKNESS) / 2 + 24
  const { width, height } = box

  const geometry = new BoxGeometry(width, height, thickness)

  const mesh = new Mesh(geometry)
  mesh.position.set(box.position.x, box.position.y, box.centerPointHeight)

  const z = 'z' in box.rotation ? box.rotation.z : box.rotation._z
  mesh.rotation.set(0, 0, z)

  return mesh
}

export const memoizedUtilityBoxMesh = weakMapMemoize(utilityBoxMesh)

function utilityBoxMeshes(boxes: Readonly<Record<string, UtilityBox>>) {
  return new Map(Object.values(boxes).map(box => [box.id, memoizedUtilityBoxMesh(box)]))
}

export const memoizedUtilityBoxMeshes = weakMapMemoize(utilityBoxMeshes)

function roofMesh(
  roof: Readonly<Roof>,
  lines: Readonly<Record<string, ElevationLine>>,
  points: Readonly<Record<string, ElevationPoint>>
) {
  const perimeterPoints: [number, number, number][] = roof.perimeterPoints.map(([x, y]) => [x, y, roof.height])
  perimeterPoints.pop() // Remove duplicate start/end point
  const perimeterEdgeIndices = perimeterPoints.reduce<[number, number][]>((indices, _, i, { length }) => {
    indices.push([i, (i + 1) % length])
    return indices
  }, [])
  const elevationPointIndices = new Map(Object.values(points).map(({ id }, i) => [id, i + perimeterPoints.length]))
  const edgeIndices = [...perimeterEdgeIndices]
  Object.values(lines).forEach(({ elevationPointIds }) => {
    const [idA, idB] = elevationPointIds
    const indexA = elevationPointIndices.get(idA ?? '')
    const indexB = elevationPointIndices.get(idB ?? '')
    if (!indexA || !indexB) return
    edgeIndices.push([indexA, indexB])
  })
  const elevationPoints = Object.values(points).reduce<[number, number, number][]>((transformedPoints, point) => {
    let wasOnPerimeter = false
    let xChange = 0
    let yChange = 0
    let basePerimeter = [0, 0]
    const position = vectorUIToModel(point.position)
    const { x, y, z } = position

    for (let i = 0; i < perimeterPoints.length; i++) {
      const p1Index = i
      const p2Index = (i + 1) % perimeterPoints.length
      const p1 = perimeterPoints[p1Index]
      const p2 = perimeterPoints[p2Index]

      const distance = Util.distanceToLineSegment(p1[0], p1[1], p2[0], p2[1], x, y)
      if (distance > modelToUI(3)) {
        continue
      }

      const p1x = Math.round(p1[0])
      const p1y = Math.round(p1[1])
      const p2x = Math.round(p2[0])
      const p2y = Math.round(p2[1])
      // If the point is on a perimeter point, its a corner. Set a tiny offset away from the corner
      if (Util.pointsAreEqual2D(p1, position, modelToUI(3))) {
        if (p1x === p2x) {
          if (p1y > p2y) {
            yChange += -0.1
          } else {
            yChange += 0.1
          }
        }
        if (p1y === p2y) {
          if (p1x > p2x) {
            xChange += -0.1
          } else {
            xChange += 0.1
          }
        }
        basePerimeter = p1
      } else if (Util.pointsAreEqual2D(p2, position, modelToUI(3))) {
        if (p1x === p2x) {
          if (p1y < p2y) {
            yChange += -0.1
          } else {
            yChange += 0.1
          }
        }
        if (p1y === p2y) {
          if (p1x < p2x) {
            xChange += -0.1
          } else {
            xChange += 0.1
          }
        }
        basePerimeter = p2
      } else {
        // If the point is on or very close to a perimeter line, find the centroid of the closest
        // triangle in the polygon and move one vector unit towards it.
        const start = new Vector3().fromArray(p1).setZ(0)
        const end = new Vector3().fromArray(p2).setZ(0)
        const edge = new Line3(start, end)
        const closetPointOnEdge = edge.closestPointToPoint(new Vector3().copy(position), true, new Vector3())
        const triangles = cdt2d(perimeterPoints, perimeterEdgeIndices, { exterior: false })
        const closestTriangle = triangles.find(indices => indices.includes(p1Index) && indices.includes(p2Index))
        const [a, b, c] = closestTriangle!
        const centroid = new Triangle(
          new Vector3().fromArray(perimeterPoints[a]).setZ(0),
          new Vector3().fromArray(perimeterPoints[b]).setZ(0),
          new Vector3().fromArray(perimeterPoints[c]).setZ(0)
        ).getMidpoint(new Vector3())
        const offset = new Vector3().subVectors(centroid, closetPointOnEdge).normalize()
        basePerimeter = [closetPointOnEdge.x, closetPointOnEdge.y]
        xChange = modelToUI(offset.x)
        yChange = modelToUI(offset.y)
      }
      wasOnPerimeter = true
      break
    }

    if (!wasOnPerimeter) {
      transformedPoints.push([x, y, z])
    } else {
      const interiorPoint = {
        x: (xChange && basePerimeter[0] + xChange) || x,
        y: (yChange && basePerimeter[1] + yChange) || y,
        z: z,
      }
      // If point is outside of roof polygon after x and y change has been applied,
      // then it is an interior corner inside the bounds of the facility. Reverse the
      // direction of x and y changes to inverse direction of displacement
      if (
        (xChange || yChange) &&
        !Util.isPointInPolygon(interiorPoint, perimeterPoints) // Inside roof bounds
      ) {
        interiorPoint.x = (xChange && basePerimeter[0] - xChange) || x
        interiorPoint.y = (yChange && basePerimeter[1] - yChange) || y
      }
      transformedPoints.push([interiorPoint.x, interiorPoint.y, interiorPoint.z])
    }
    return transformedPoints
  }, [])

  const allPoints = perimeterPoints.concat(elevationPoints)
  const points3d = allPoints.map(([x, y, z]) => new Vector3(x, y, z))
  const triangles = cdt2d(allPoints, edgeIndices, { exterior: false })

  const geometry = new BufferGeometry().setFromPoints(points3d)
  geometry.setIndex(triangles.flat())
  geometry.computeVertexNormals()

  const mesh = new Mesh(geometry, new MeshBasicMaterial({ side: DoubleSide }))
  mesh.name = `roof ${roof.id}`
  return mesh
}

export const memoizedRoofMesh = weakMapMemoize(roofMesh)

function roofMeshes(
  roofs: Readonly<Record<string, Roof>>,
  lines: Readonly<Record<string, ElevationLine>>,
  points: Readonly<Record<string, ElevationPoint>>
) {
  return new Map(Object.values(roofs).map(roof => [roof.id, memoizedRoofMesh(roof, lines, points)]))
}

export const memoizedRoofMeshes = weakMapMemoize(roofMeshes)

function columnsMesh({
  hasColumns,
  height,
  beamDepth,
  isCylinder,
  columnWidth,
  beamModels,
  columnLineModels,
  perimeterPoints,
  beamRotation,
}: Readonly<RoofSection>) {
  if (!hasColumns) return
  const isVertical = beamRotation === 0
  const columnHeight = height - beamDepth
  const columnPositions = beamModels?.reduce<Vector3[]>((intersections, beam) => {
    const edges = perimeterPoints.reduce<Edge[]>((acc, currPoint, i, arr) => {
      if (i === 0) return acc
      const prevPoint = arr[i - 1]
      acc.push({ p1: [currPoint[0], currPoint[1]], p2: [prevPoint[0], prevPoint[1]] })
      return acc
    }, [])
    columnLineModels?.forEach(columnLine => {
      if (isVertical) {
        const isInFacility = isPointInPolygon([beam.position.x, columnLine.position.y], edges)
        if (isInFacility) intersections.push(new Vector3(beam.position.x, columnLine.position.y, columnHeight / 2))
      } else {
        const isInFacility = isPointInPolygon([columnLine.position.x, beam.position.y], edges)
        if (isInFacility) intersections.push(new Vector3(columnLine.position.x, beam.position.y, columnHeight / 2))
      }
    })
    return intersections
  }, [])
  const scene = new Object3D()
  const geometry = isCylinder
    ? new CylinderGeometry(columnWidth / 2, columnWidth / 2, columnHeight)
    : new BoxGeometry(columnWidth, columnWidth, columnHeight)
  columnPositions?.forEach(position => {
    const mesh = new Mesh(geometry)
    mesh.position.copy(position)
    mesh.rotation.x = isCylinder ? Math.PI / 2 : 0
    scene.add(mesh)
  })
  scene.updateMatrixWorld()
  return scene
}

const memoizedColumnsMesh = weakMapMemoize(columnsMesh)

function columnsMeshes(roofSections: Readonly<Record<string, RoofSection>>) {
  return new Map(Object.values(roofSections).map(roofSection => [roofSection.id, memoizedColumnsMesh(roofSection)]))
}

export const memoizedColumnsMeshes = weakMapMemoize(columnsMeshes)
